// mp-countdown (Mobile Pixels custom, ported from shopify-theme-mobilepixels)
// Header countdown bar. show/end datetimes are interpreted in America/Los_Angeles
// regardless of the visitor's timezone; closing hides it for the rest of the session.

const TIMEZONE = "America/Los_Angeles";

// Offset (ms) between the given timezone's wall clock and UTC at instant `ts`.
function timezoneOffset(ts) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TIMEZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ts));
  const p = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return Date.UTC(+p.year, p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - ts;
}

// Parse "yyyy-MM-dd HH:mm:ss" as a wall-clock time in TIMEZONE → Date, or null.
function parseInTimezone(str) {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(str || "");
  if (!m) return null;
  const asUTC = Date.UTC(+m[1], m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  // Two passes converge on the correct offset across DST boundaries.
  let ts = asUTC - timezoneOffset(asUTC);
  ts = asUTC - timezoneOffset(ts);
  return new Date(ts);
}

class MPCountdown extends HTMLElement {
  constructor() {
    super();
    if (window.sessionStorage.getItem("mpCountdownClosed") === "true") {
      this.remove();
      return;
    }
    this._interval = null;
    this._end = parseInTimezone(this.getAttribute("end"));
    this._show = parseInTimezone(this.getAttribute("show"));

    this._close = this.querySelector(".close-btn");
    if (this._close) {
      this._close.addEventListener("click", this._onCloseClicked.bind(this));
    }

    this._days = this.querySelector(".days");
    this._daysSem = this.querySelector(".days-sem");
    this._hours = this.querySelector(".hours");
    this._minutes = this.querySelector(".minutes");
    this._seconds = this.querySelector(".seconds");
  }

  connectedCallback() {
    if (!this._isValidDate(this._end)) {
      this.disconnectedCallback();
      return;
    }

    if (this._isValidDate(this._show) && this._now() < this._show) {
      this.disconnectedCallback();
      return;
    }
    this._interval = setInterval(this._update.bind(this), 1e3);
    this._update();
  }

  _onCloseClicked() {
    this.style.height = "0";
    this.addEventListener(
      "transitionend",
      function () {
        this.disconnectedCallback();
        window.sessionStorage.setItem("mpCountdownClosed", "true");
      },
      { once: true }
    );
  }

  _now() {
    return new Date();
  }

  _update() {
    const now = this._now();
    if (now >= this._end) {
      this.disconnectedCallback();
    } else {
      const [days, hours, minutes, seconds] = this._formatDiff(this._end - now);
      if (days === "00") {
        this._days.parentElement.style.display = "none";
        this._daysSem.style.display = "none";
      } else {
        this._days.innerHTML = days;
      }
      this._hours.innerHTML = hours;
      this._minutes.innerHTML = minutes;
      this._seconds.innerHTML = seconds;
    }
  }

  _isValidDate(d) {
    return d instanceof Date && !isNaN(d.getTime());
  }

  _formatDiff(diff) {
    const formatNumber = (num) => {
      return num < 10 ? `0${num}` : num;
    };
    const days = formatNumber(Math.floor(diff / (1000 * 60 * 60 * 24)));
    const hours = formatNumber(Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
    const minutes = formatNumber(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)));
    const seconds = formatNumber(Math.floor((diff % (1000 * 60)) / 1000));
    return [days, hours, minutes, seconds];
  }

  disconnectedCallback() {
    this._interval && clearInterval(this._interval);
    this.remove();
  }
}

if (!customElements.get("mp-countdown")) {
  customElements.define("mp-countdown", MPCountdown);
}
